import type { ResultadoCotizacion as Resultado } from '../api'
import { num, pct, usd } from '../formato'

/** Sistema trifasico: tension, strings, potencia AC y relacion DC/AC. */
export function SistemaIndustrial({ resultado }: { resultado: Resultado }) {
  const d = resultado.dimensionado
  return (
    <div className="tarjeta">
      <h2>Sistema trifásico</h2>
      <dl className="lista-datos">
        <dt>Potencia</dt>
        <dd>
          {num(d.potencia_kwp)} kWp · {num(d.potencia_ac_kw ?? 0)} kW AC (DC/AC {num(d.relacion_dc_ac ?? 0)})
        </dd>
        <dt>Paneles</dt>
        <dd>
          {d.paneles} × {d.panel.nombre}
        </dd>
        <dt>Strings</dt>
        <dd>
          {d.strings} de hasta {d.paneles_por_string} paneles
        </dd>
        <dt>Inversores</dt>
        <dd>
          {d.inversores} × {d.inversor.nombre}
        </dd>
        <dt>Conexión</dt>
        <dd>
          {d.tension_v} V trifásico · estructura {d.estructura === 'losa' ? 'de losa' : 'de techo'}
        </dd>
        <dt>Generación</dt>
        <dd>{num(d.generacion_anual_kwh)} kWh/año</dd>
        <dt>Consumo · demanda</dt>
        <dd>
          {num(d.consumo_kwh_mes)} kWh/mes · {num(d.demanda_kw ?? 0)} kW
        </dd>
      </dl>
    </div>
  )
}

/** La tarifa con demanda, por partes, y de donde sale el ahorro. */
export function SupuestosIndustrial({ resultado }: { resultado: Resultado }) {
  const { financiero: f, tarifa_demanda: t } = resultado
  if (!t) return null
  return (
    <div className="tarjeta">
      <h2>Tarifa y ahorro</h2>
      <dl className="lista-datos">
        <dt>Tarifa</dt>
        <dd>
          {t.nombre}{' '}
          <span className={`insignia ${t.editada ? 'insignia-pendiente' : 'insignia-cliente'}`}>{t.editada ? 'editada a mano' : 'pliego'}</span>
          <small className="suave bloque">{t.fuente}</small>
        </dd>
        <dt>Energía</dt>
        <dd>${t.energia_usd_kwh.toFixed(4)}/kWh</dd>
        <dt>Demanda</dt>
        <dd>${t.demanda_usd_kw_mes.toFixed(3)}/kW-mes</dd>
        <dt>Comercialización</dt>
        <dd>${t.comercializacion_usd_mes.toFixed(3)}/mes (no es ahorro)</dd>
        <dt>Ahorro en energía</dt>
        <dd>
          {usd(f.ahorro_energia_anual ?? 0)}/año · {num(t.kwh_evitados_mes)} kWh/mes
        </dd>
        <dt>Ahorro en demanda</dt>
        <dd>
          {usd(f.ahorro_demanda_anual ?? 0)}/año · {num(t.kw_demanda_evitada)} kW menos de pico
        </dd>
        <dt>Planilla estimada</dt>
        <dd>
          {usd(t.planilla_actual_mes)} → {usd(t.planilla_con_sistema_mes)} al mes
        </dd>
        <dt>Tarifa equivalente</dt>
        <dd>
          ${f.tarifa_usd_kwh.toFixed(4)}/kWh generado · LCOE ${f.lcoe.toFixed(4)}
        </dd>
        <dt>Beneficio/costo · ROI</dt>
        <dd>
          {num(f.costo_beneficio)} · {pct(f.roi, 0)}
        </dd>
        <dt>Reposición inversores</dt>
        <dd>{usd(f.reposicion_inversor_usd)} (año 12)</dd>
      </dl>
    </div>
  )
}

/** Lo que el motor pide revisar antes de mandar la proforma. */
export function Avisos({ avisos }: { avisos?: string[] }) {
  if (!avisos?.length) return null
  return (
    <div className="tarjeta avisos">
      <h2>Revisar antes de enviar</h2>
      <ul>
        {avisos.map((a) => (
          <li key={a}>{a}</li>
        ))}
      </ul>
    </div>
  )
}
